import { Contact } from "../components/Contact";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ArrowRight, MapPin, Calendar, MessageCircle } from "lucide-react";
import { SEO } from "../components/SEO";

// Move faqs above its first use
const faqs = [
  {
    question: "How long does a typical Zoho implementation take?",
    answer: "Implementation timelines vary based on complexity. Simple setups take 2-4 weeks, while comprehensive Zoho One implementations may take 6-12 weeks. We provide detailed timelines during our initial consultation."
  },
  {
    question: "Do you provide training for our team?",
    answer: "Yes! We include comprehensive training as part of our implementation packages. This covers user training, admin training, and ongoing support documentation to ensure your team can effectively use the system."
  },
  {
    question: "What ongoing support do you provide?",
    answer: "We offer 24/7 technical support, regular system health checks, optimization recommendations, and assistance with new feature adoption. Our support packages are tailored to your specific needs."
  },
  {
    question: "Can you integrate Zoho with our existing systems?",
    answer: "Absolutely! We specialize in integrating Zoho applications with various third-party systems including ERPs, payment gateways, e-commerce platforms, and custom applications through APIs and webhooks."
  },
  {
    question: "What is your pricing model?",
    answer: "Our pricing is project-based and depends on the scope of implementation, number of applications, customization requirements, and ongoing support needs. We provide transparent, detailed quotes after understanding your requirements."
  }
];

export default function ContactPage() {
  // FAQPage schema for the FAQ section
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
  const contactMethods = [
    {
      icon: MessageCircle,
      title: "WhatsApp Chat",
      description: "Quick chat for immediate assistance",
      availability: "Mon-Fri, 9 AM - 9 PM IST",
      action: "Start Chat",
      href: "https://wa.me/918447103081"
    },
    {
      icon: Calendar,
      title: "Schedule Meeting",
      description: "Book a detailed consultation session",
      availability: "Available 7 days a week",
      action: "Book Now",
      href: "https://consult.optiwizer.com/#/book_consultation"
    }
  ];

  const officeLocation = {
    city: "New Delhi",
    address: "Pitampura, New Delhi 110034, Delhi",
    type: "Head Office",
    timezone: "IST (UTC +5:30)",
    services: ["Development", "Consulting", "Support", "Training"],
    mapsUrl: "https://maps.google.com/?q=Pitampura+New+Delhi+110034"
  };

  const supportChannels = [
    {
      title: "Technical Support",
      description: "Get help with Zoho configurations, integrations, and troubleshooting",
      responseTime: "Within 2 hours",
      availability: "24/7",
      contactMethod: "WhatsApp Support",
      href: "https://wa.me/918447103081?text=I need technical support"
    },
    {
      title: "Sales Inquiries",
      description: "Discuss your requirements and get custom pricing for Zoho solutions",
      responseTime: "Within 1 hour",
      availability: "Mon-Fri, 9 AM - 7 PM",
      contactMethod: "WhatsApp Sales",
      href: "https://wa.me/918447103081?text=I'm interested in Zoho solutions"
    },
    {
      title: "Training & Workshops",
      description: "Schedule team training sessions and Zoho skill development programs",
      responseTime: "Within 4 hours", 
      availability: "Mon-Fri, 10 AM - 6 PM",
      contactMethod: "Schedule Consultation",
      href: "https://consult.optiwizer.com/#/book_consultation"
    },
    {
      title: "Partnership Opportunities",
      description: "Explore collaboration opportunities and partner programs",
      responseTime: "Within 24 hours",
      availability: "Mon-Fri, 9 AM - 6 PM",
      contactMethod: "Book Meeting",
      href: "https://consult.optiwizer.com/#/book_consultation"
    }
  ];

  const quickActions = [
    {
      title: "Free Consultation",
      description: "30-minute strategy session to discuss your Zoho needs",
      duration: "30 mins",
      price: "Free",
      action: "Book Now",
      href: "https://consult.optiwizer.com/#/book_consultation"
    },
    {
      title: "Zoho Health Check",
      description: "Comprehensive audit of your current Zoho setup",
      duration: "2-3 days",
      price: "₹15,000",
      action: "Schedule Audit",
      href: "https://wa.me/918447103081?text=I want to schedule a Zoho Health Check"
    },
    {
      title: "Custom Demo",
      description: "Personalized demo based on your business requirements",
      duration: "45 mins",
      price: "Free",
      action: "Request Demo",
      href: "https://wa.me/918447103081?text=I want to request a custom demo"
    }
  ];

  return (
    <>
      <SEO
        title="Contact Optiwizer Technologies – Zoho Consultation & Support"
        description="Get in touch with Optiwizer Technologies for Zoho implementation, support, and consulting. Quick response and expert assistance."
        canonical="https://optiwizer.com/contact"
        ogImage="/assets/logos/optiwizer-logo.png"
      />
      {/* FAQPage Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
      {/* Hero Section */}
      <section className="pt-24 lg:pt-28 pb-20 lg:pb-24 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50" aria-labelledby="contact-hero-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 lg:mb-16">
            <Badge className="mb-4 bg-blue-100 text-blue-800">Get in Touch</Badge>
            <h1 id="contact-hero-heading" className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-gray-900 mb-8 leading-tight">
              Let's Transform Your Business
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 block mt-2">
                Together
              </span>
            </h1>
            <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Ready to streamline your operations with Zoho? Our expert team is here to guide you 
              through every step of your digital transformation journey.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://consult.optiwizer.com/#/book_consultation">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white text-lg px-8 py-4">
                  <Calendar className="mr-2 h-5 w-5" />
                  Book Free Consultation
                </Button>
              </a>
              <a href="https://wa.me/918447103081">
                <Button size="lg" variant="outline" className="text-lg px-8 py-4">
                  <MessageCircle className="mr-2 h-5 w-5" />
                  WhatsApp Chat
                </Button>
              </a>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {quickActions.map((action, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-6">
                  <h3 className="font-bold text-gray-900 mb-2">{action.title}</h3>
                  <p className="text-sm text-gray-600 mb-4">{action.description}</p>
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-sm text-gray-500">{action.duration}</span>
                    <Badge className={action.price === "Free" ? "bg-green-100 text-green-800" : "bg-blue-100 text-blue-800"}>
                      {action.price}
                    </Badge>
                  </div>
                  <a href={action.href} target="_blank" rel="noopener noreferrer">
                    <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                      {action.action}
                    </Button>
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-16 lg:py-20 bg-white" aria-labelledby="contact-methods-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 lg:mb-16">
            <Badge className="mb-4 bg-purple-100 text-purple-800">Contact Options</Badge>
            <h2 id="contact-methods-heading" className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Choose Your Preferred Way to Connect
            </h2>
            <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
              We offer multiple ways to get in touch. Pick the method that works best for you 
              and our team will respond promptly to assist with your Zoho requirements.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 max-w-4xl mx-auto">
            {contactMethods.map((method, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6">
                    <method.icon className="h-8 w-8 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {method.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    {method.description}
                  </p>
                  <div className="text-xs text-gray-500 mb-4">{method.availability}</div>
                  <a href={method.href} target="_blank" rel="noopener noreferrer">
                    <Button size="sm" className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                      {method.action}
                    </Button>
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Main Contact Form */}
      <Contact />

      {/* Office Location */}
      <section className="py-16 lg:py-20 bg-gray-50" aria-labelledby="office-location-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 lg:mb-16">
            <Badge className="mb-4 bg-green-100 text-green-800">Our Location</Badge>
            <h2 id="office-location-heading" className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Visit Our Office in New Delhi
            </h2>
            <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
              Located in the heart of New Delhi, we're always available to provide 
              in-person consultations and support for your Zoho implementation needs.
            </p>
          </div>

          <div className="max-w-2xl mx-auto">
            <Card className="hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">{officeLocation.city}</h3>
                    <Badge className="bg-blue-100 text-blue-800">
                      {officeLocation.type}
                    </Badge>
                  </div>
                  <MapPin className="h-6 w-6 text-blue-600" />
                </div>

                <div className="space-y-4">
                  <div>
                    <div className="text-sm font-medium text-gray-700 mb-1">Address</div>
                    <div className="text-sm text-gray-600">{officeLocation.address}</div>
                  </div>

                  <div>
                    <div className="text-sm font-medium text-gray-700 mb-1">Timezone</div>
                    <div className="text-sm text-gray-600">{officeLocation.timezone}</div>
                  </div>

                  <div>
                    <div className="text-sm font-medium text-gray-700 mb-2">Services Available</div>
                    <div className="flex flex-wrap gap-1">
                      {officeLocation.services.map((service, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {service}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>

              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Support Channels */}
      <section className="py-16 lg:py-20 bg-white" aria-labelledby="support-channels-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 lg:mb-16">
            <Badge className="mb-4 bg-yellow-100 text-yellow-800">Support Channels</Badge>
            <h2 id="support-channels-heading" className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Dedicated Support for Every Need
            </h2>
            <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
              Our specialized support teams are ready to assist you with technical issues, 
              sales inquiries, training needs, and partnership opportunities.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {supportChannels.map((channel, index) => (
              <Card key={index} className="hover:shadow-lg transition-all duration-300">
                <CardHeader>
                  <CardTitle className="text-lg font-bold text-gray-900">
                    {channel.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-gray-600 mb-4">
                    {channel.description}
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <div className="text-xs font-medium text-gray-700 mb-1">Response Time</div>
                      <div className="text-sm text-green-600 font-medium">{channel.responseTime}</div>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-gray-700 mb-1">Availability</div>
                      <div className="text-sm text-blue-600 font-medium">{channel.availability}</div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">{channel.contactMethod}</div>
                    <a href={channel.href}>
                      <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
                        Contact
                        <ArrowRight className="ml-2 h-3 w-3" />
                      </Button>
                    </a>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 lg:py-20 bg-gray-50" aria-labelledby="faq-heading">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 lg:mb-16">
            <Badge className="mb-4 bg-blue-100 text-blue-800">FAQ</Badge>
            <h2 id="faq-heading" className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg lg:text-xl text-gray-600">
              Quick answers to common questions about our Zoho implementation 
              and consulting services.
            </p>
          </div>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <Card key={index} className="hover:shadow-lg transition-all duration-300">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    {faq.question}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">
              Don't see your question answered here?
            </p>
            <a href="https://wa.me/918447103081?text=I have a question about Zoho services">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                Ask Your Question
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-20 bg-gradient-to-r from-gray-900 to-blue-900" aria-labelledby="contact-cta-heading">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 id="contact-cta-heading" className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-lg lg:text-xl text-gray-300 mb-8">
            Don't wait to transform your business operations. Schedule your free consultation 
            today and discover how Zoho can accelerate your growth.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://consult.optiwizer.com/#/book_consultation">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white text-lg px-8 py-4">
                <Calendar className="mr-2 h-5 w-5" />
                Book Free Consultation
              </Button>
            </a>
            <a href="https://wa.me/918447103081">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 text-lg px-8 py-4">
                <MessageCircle className="mr-2 h-5 w-5" />
                WhatsApp Chat
              </Button>
            </a>
          </div>
        </div>
      </section>
    </>
  );
}